import { Box, Dialog, DialogContent, DialogFooter, DialogHeader, Modal, Text, Button, Inline } from '@ui-lib';
import { useEffect, useState } from 'react';
import { useActorPlan } from '@hooks/actor/use-actor-plan';
import { useRouter } from 'next/router';
import { usePremiumUpgradeFlow } from 'pages/onboarding/upgrade-flow-selection';
import { useUser } from '@context/user-context';

import { noop } from 'lodash';

export const LoggedInPremiumUpgradeDialog: React.FC = () => {
  const router = useRouter();
  const { loading, planType } = useActorPlan();
  const { loading: loadingUser, user, actor } = useUser();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [loadingAction, setLoadingAction] = useState<'idle' | 'newAccount' | 'checkout'>('idle');
  const { isInUpgradeSession, endUpgradeSession, startUpgradeCheckout } = usePremiumUpgradeFlow();

  const isTeamMember =
    actor?.profiles?.find((profile) => profile.workspace?.type === 'TEAM')?.role === 'MEMBER';

  // Show this dialog if the user is in an upgrade session
  useEffect(() => {
    if (!router.isReady) return;

    if (isInUpgradeSession) {
      setShowDialog(true);
    } else {
      setShowDialog(false);
    }
  }, [router, isInUpgradeSession]);

  if (loading || !router.isReady || loadingUser) {
    // Wait until load before appearing.
    return <></>;
  }

  async function handleCreateNewAccount() {
    setLoadingAction('newAccount');
    // Log user out when they get there.
    router.push('/onboarding/customize-card?logout=true');
  }

  async function handleDismissUpgrade() {
    endUpgradeSession();
    setShowDialog(false);
  }

  async function handleConfirmUpgrade() {
    if (!user) return;
    setLoadingAction('checkout');
    startUpgradeCheckout({ userId: user.userId });
  }

  return (
    <Modal open={showDialog} handleClose={noop}>
      <div
        style={{
          maxWidth: '565px',
        }}
      >
        <Dialog>
          {planType === 'free' && (
            <>
              <DialogHeader title="Premium trial" onClose={handleDismissUpgrade} />
              <DialogContent>
                <Box paddingY="medium">
                  <Text size="medium">
                    You are logged in as <strong>{user?.signInEmailAddress}</strong>. Would you like to trial
                    Blinq Premium on this account or create a new account?
                  </Text>
                </Box>
              </DialogContent>
              <DialogFooter>
                <Inline gap="medium">
                  <Button
                    loading={loadingAction === 'newAccount'}
                    variant="outline"
                    onClick={handleCreateNewAccount}
                  >
                    Create new account
                  </Button>
                  <Button loading={loadingAction === 'checkout'} onClick={handleConfirmUpgrade}>
                    Trial Premium on this account
                  </Button>
                </Inline>
              </DialogFooter>
            </>
          )}
          {planType === 'premium' && !isTeamMember && (
            <>
              <DialogHeader title="This account already has a Premium subscription" />
              <DialogContent>
                <Box paddingY="medium">
                  <Text size="medium">Would you like to trial Blinq Premium on a new account?</Text>
                </Box>
              </DialogContent>
              <DialogFooter>
                <Inline gap="medium">
                  <Button
                    loading={loadingAction === 'newAccount'}
                    onClick={handleCreateNewAccount}
                    variant="outline"
                  >
                    Trial Premium on new account
                  </Button>
                  <Button onClick={handleDismissUpgrade}>No, stay on this account</Button>
                </Inline>
              </DialogFooter>
            </>
          )}
          {(planType === 'team' || (planType === 'premium' && isTeamMember)) && (
            <>
              <DialogHeader title="This account has a Business subscription" />
              <DialogContent>
                <Box paddingY="medium">
                  <Text size="medium">
                    Business subscriptions include all the benefits of a Premium subscription. Would you like
                    to trial Blinq Premium on a new account?
                  </Text>
                </Box>
              </DialogContent>
              <DialogFooter>
                <Inline gap="medium">
                  <Button
                    loading={loadingAction === 'newAccount'}
                    onClick={handleCreateNewAccount}
                    variant="outline"
                  >
                    Trial Premium on new account
                  </Button>
                  <Button onClick={handleDismissUpgrade}>No, stay on this account</Button>
                </Inline>
              </DialogFooter>
            </>
          )}
        </Dialog>
      </div>
    </Modal>
  );
};
